import { useState } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Upload, Download, AlertCircle, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import downloadFile from "@/utils/fileDownloader";

const BulkImport = () => {
  const { user } = useAuth();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.xlsx') && !selectedFile.name.endsWith('.xls')) {
        toast.error("Please upload an Excel file (.xlsx or .xls)");
        return;
      }
      setFile(selectedFile);
      setResult(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("Please select a file first");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('admin_id', user.id);
    formData.append('hostel_id', user.hostel_id);

    try {
      const response = await axios.post(`${API}/students/bulk-import`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setResult(response.data);
      toast.success(response.data.message);
      setFile(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to import students");
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    const template = [
      ['name', 'age', 'class_name', 'room_number', 'parent_phone', 'interests'],
      ['John Doe', 12, '7th Grade', '101', '+1234567893', 'Sports, Reading'],
      ['Jane Smith', 13, '8th Grade', '102', '+1234567894', 'Music, Art']
    ];
    
    const csvContent = template.map(row => row.join(',')).join('\n');
    downloadFile(csvContent, 'student_import_template.csv', 'text/csv');
  };

  return (
    <div data-testid="bulk-import" className="space-y-6">
      <h1 className="text-2xl font-bold" style={{ color: "#2C5F7F" }}>Bulk Import Students</h1>

      <Card className="glass-effect">
        <CardHeader>
          <CardTitle>Download Template</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-gray-600">
            Download the Excel template with the required format for importing students.
          </p>
          <div className="bg-blue-50 p-4 rounded-lg">
            <p className="text-sm font-medium mb-2">Required Columns:</p>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• <strong>name</strong> - Student full name</li>
              <li>• <strong>age</strong> - Student age (number)</li>
              <li>• <strong>class_name</strong> - Class/Grade</li>
              <li>• <strong>room_number</strong> - Room assignment</li>
              <li>• <strong>parent_phone</strong> - Parent phone number</li>
              <li>• <strong>interests</strong> - Comma-separated interests (optional)</li>
            </ul>
          </div>
          <Button
            data-testid="download-template-btn"
            onClick={downloadTemplate}
            className="btn-primary text-white"
          >
            <Download className="w-4 h-4 mr-2" />
            Download Template
          </Button>
        </CardContent>
      </Card>

      <Card className="glass-effect">
        <CardHeader>
          <CardTitle>Upload Excel File</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
            <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              className="hidden"
              id="file-upload"
              data-testid="file-input"
            />
            <label
              htmlFor="file-upload"
              className="cursor-pointer text-blue-600 hover:text-blue-700 font-medium"
            >
              Click to upload
            </label>
            <p className="text-sm text-gray-500 mt-2">or drag and drop</p>
            <p className="text-xs text-gray-400 mt-1">Excel files (.xlsx, .xls)</p>
          </div>

          {file && (
            <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="text-sm font-medium">{file.name}</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setFile(null)}
              >
                Remove
              </Button>
            </div>
          )}

          <Button
            data-testid="upload-btn"
            onClick={handleUpload}
            disabled={!file || uploading}
            className="w-full btn-primary text-white"
          >
            {uploading ? "Importing..." : "Import Students"}
          </Button>
        </CardContent>
      </Card>

      {result && (
        <Card className="glass-effect">
          <CardHeader>
            <CardTitle>Import Results</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-6 h-6 text-green-600" />
              <span className="text-lg font-semibold">
                Successfully imported {result.imported} student{result.imported !== 1 ? 's' : ''}
              </span>
            </div>

            {result.errors && result.errors.length > 0 && (
              <div className="bg-red-50 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                  <span className="font-medium text-red-800">Errors:</span>
                </div>
                <ul className="text-sm text-red-700 space-y-1">
                  {result.errors.map((error, index) => (
                    <li key={index}>• {error}</li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BulkImport;
